import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RemoveList } from '@saleshandy/icons';
import Modal from '../../../../shared/design-system/components/atoms/modal';
import TagAutosuggest2 from '../../../../shared/design-system/components/molecules/tag-autosuggest-2';
import { RenderMode } from '../../../../shared/design-system/components/molecules/tag-autosuggest-2/tag-autosuggest-2';
import { AddToListData } from '../add-to-list-dropdown/add-to-list-dropdown';

type RemoveFromListModalProps = {
  show: boolean;
  onClose: VoidFunction;
  lists: AddToListData[];
  onRemoveFromList?: (listIds: number[], onSuccess?: () => void) => void;
  isSubmitLoading?: boolean;
};

const RemoveFromListModal: React.FC<RemoveFromListModalProps> = ({
  show,
  onClose,
  lists,
  onRemoveFromList,
  isSubmitLoading = false,
}) => {
  const { t } = useTranslation();
  const [selectedLists, setSelectedLists] = useState<any[]>([]);
  const limitExceededRef = useRef(false);
  const handleClose = () => onClose();

  useEffect(() => {
    if (!show) {
      setSelectedLists([]);
    }
  }, [show]);

  const listTags = useMemo(
    () =>
      (lists ?? []).map((l) => ({
        id: l.id,
        name: l.label,
      })),
    [lists],
  );

  const disabledListIds = useMemo(
    () => (lists ?? []).filter((l) => l.isDefault).map((l) => l.id),
    [lists],
  );

  const handleSubmit = () => {
    const ids = selectedLists
      .map((x) => x?.id)
      .filter((id: any) => typeof id === 'number');

    if (ids.length > 0) {
      onRemoveFromList?.(ids, () => {
        setSelectedLists([]);
        onClose();
      });
    }
  };

  return (
    <Modal
      show={show}
      className="remove-from-list-modal"
      titleContent={
        <div className="d-flex modal-header-title">
          <RemoveList />
          <div className="pl-2">Remove From List</div>
        </div>
      }
      onClose={handleClose}
      onCancel={handleClose}
      onHide={handleClose}
      showCloseIcon={false}
      submitButtonText={t('labels.apply')}
      isSubmitDisabled={selectedLists.length === 0 || isSubmitLoading}
      isSubmitLoading={isSubmitLoading}
      onSubmit={handleSubmit}
      backdrop="static"
      extraModalProps={{
        enforceFocus: false,
        'aria-labelledby': 'contained-modal-title-vcenter',
      }}
    >
      <div className="overflow-hidden saved-tab-tags remove-tag-modal remove-from-list-modal__input">
        <TagAutosuggest2
          selectedProspects={[]}
          tags={listTags}
          tagsToAdd={selectedLists}
          setTagsToAdd={setSelectedLists}
          renderMode={RenderMode.Inline}
          showAddedTags={false}
          showCreateSuggestion={false}
          defaultSuggestionOpened={true}
          restrictExistingOnly={true}
          showCheckboxSuggestions={true}
          placeholder="Enter list"
          disabledTagIds={disabledListIds}
          disabledTagTooltip="Default created list"
          disableDefaultTagsBehavior={true}
          styles={{
            suggestionsContainer: {
              position: 'relative',
              left: '0',
              right: '0',
              zIndex: 1,
            },
            suggestionsContainerOpen: {
              marginTop: '0px',
              minHeight: '100px',
              maxHeight: '200px',
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: '12px 0px',
            },
            suggestionsList: {
              padding: 0,
              margin: 0,
              listStyleType: 'none',
            },
          }}
          limitExceededRef={limitExceededRef}
        />
        {/* {listTags.length === 0 && (
          <div className="no-list-message">Please add the list to remove</div>
        )} */}
      </div>
    </Modal>
  );
};

export default RemoveFromListModal;
